import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { OfferMetadata } from "@/hooks/use-project-detail";
import { useUpdateOfferMetadata } from "@/hooks/use-budget-lines";

interface Props {
  projectId: string;
  metadata: OfferMetadata | null;
  budgetTotal: number;
  overheadAmount: number;
  discountAmount: number;
  /** Herav-sum av reise-linjer (inngår i budgetTotal / overhead, VIB-2086). */
  travelTotal: number;
  calculatedTotal: number;
  editing: boolean;
}

/**
 * Tilbudssum-kortet. I edit-modus blir overhead/rabatt redigerbare og
 * lagres til project_offer_metadata på blur. projects.total_amount
 * synkroniseres automatisk via useUpdateOfferMetadata når offer_total
 * settes.
 */
export function OfferMetadataEditCard({
  projectId,
  metadata,
  budgetTotal,
  overheadAmount,
  discountAmount,
  travelTotal,
  calculatedTotal,
  editing,
}: Props) {
  const updateMut = useUpdateOfferMetadata();
  const overheadPctValue = metadata?.overhead_pct ?? 15;
  const discountPctValue = metadata?.discount_pct ?? 0;
  const discountAmountValue = metadata?.discount_amount ?? 0;
  const startupValue = metadata?.startup_invoice_pct ?? 60;

  const [overheadPct, setOverheadPct] = useState<string>(String(overheadPctValue));
  const [discountPct, setDiscountPct] = useState<string>(String(discountPctValue));
  const [discountKr, setDiscountKr] = useState<string>(String(discountAmountValue));
  const [startupPct, setStartupPct] = useState<string>(String(startupValue));

  // useState seeder kun ved første render. metadata loades asynkront fra
  // useProjectDetail, så lokal state må synkes når prop endres — ellers
  // viser inputene gamle verdier og ser "ikke redigerbare" ut.
  useEffect(() => setOverheadPct(String(overheadPctValue)), [overheadPctValue]);
  useEffect(() => setDiscountPct(String(discountPctValue)), [discountPctValue]);
  useEffect(() => setDiscountKr(String(discountAmountValue)), [discountAmountValue]);
  useEffect(() => setStartupPct(String(startupValue)), [startupValue]);

  const apply = (patch: Parameters<typeof updateMut.mutate>[0]["patch"]) => {
    // Beregn ny offer_total basert på patchen så projects.total_amount
    // ikke blir stale når overhead/rabatt endres.
    const nextOverheadPct = patch.overhead_pct ?? overheadPctValue;
    const nextDiscountPct = patch.discount_pct ?? discountPctValue;
    const nextDiscountKr = patch.discount_amount ?? discountAmountValue;
    const nextOverhead = Math.round(budgetTotal * (nextOverheadPct / 100));
    const nextDiscount =
      nextDiscountKr > 0
        ? nextDiscountKr
        : Math.round((budgetTotal + nextOverhead) * (nextDiscountPct / 100));
    // VIB-2086: reise inngår i budgetTotal (overhead-grunnlaget) og skal
    // ikke adderes på nytt.
    const nextTotal = budgetTotal + nextOverhead - nextDiscount;
    updateMut.mutate({
      project_id: projectId,
      patch: { ...patch, offer_total: nextTotal },
    });
  };

  const overheadPctDisplay =
    metadata?.overhead_pct ??
    (budgetTotal > 0 ? Math.round((overheadAmount / budgetTotal) * 100) : 0);
  const discountPctDisplay =
    metadata?.discount_pct ??
    (budgetTotal > 0 ? Math.round((discountAmount / budgetTotal) * 100) : 0);

  return (
    <div className="bg-card p-5 pb-[22px]">
      <p className="text-[11.5px] font-medium uppercase tracking-[0.08em] opacity-60">
        Tilbudssum
      </p>
      <p className="font-display font-semibold text-[32px] leading-[1.05] tracking-[-0.02em] tabular-nums mt-2">
        {formatNOK(calculatedTotal)}
      </p>
      <div className="mt-3 space-y-1.5 text-[12.5px] text-muted-foreground">
        <div className="flex justify-between items-center">
          <span>Budsjettlinjer</span>
          <span className="tabular-nums">{formatNOK(budgetTotal)}</span>
        </div>
        <div className="flex justify-between items-center gap-2">
          <span className="flex items-center gap-2">
            + Overhead
            {editing ? (
              <Input
                type="number"
                step="0.5"
                className="h-7 w-16 px-2 text-xs tabular-nums"
                value={overheadPct}
                onChange={(e) => setOverheadPct(e.target.value)}
                onBlur={() => {
                  const next = Number(overheadPct);
                  if (next !== overheadPctValue) apply({ overhead_pct: next });
                }}
              />
            ) : (
              overheadPctDisplay > 0 && `(${overheadPctDisplay}%)`
            )}
          </span>
          <span className="tabular-nums">{formatNOK(overheadAmount)}</span>
        </div>
        <div className="flex justify-between items-center gap-2">
          <span className="flex items-center gap-2">
            − Rabatt
            {editing ? (
              <>
                <Input
                  type="number"
                  step="0.5"
                  placeholder="%"
                  className="h-7 w-14 px-2 text-xs tabular-nums"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(e.target.value)}
                  onBlur={() => {
                    const next = Number(discountPct);
                    if (next !== discountPctValue) apply({ discount_pct: next });
                  }}
                />
                <Input
                  type="number"
                  placeholder="kr"
                  className="h-7 w-20 px-2 text-xs tabular-nums"
                  value={discountKr}
                  onChange={(e) => setDiscountKr(e.target.value)}
                  onBlur={() => {
                    const next = Number(discountKr);
                    if (next !== discountAmountValue)
                      apply({ discount_amount: next });
                  }}
                />
              </>
            ) : (
              discountPctDisplay > 0 && `(${discountPctDisplay}%)`
            )}
          </span>
          <span
            className={cn(
              "tabular-nums",
              discountAmount > 0 && "text-destructive",
            )}
          >
            {discountAmount > 0 ? `−${formatNOK(discountAmount)}` : formatNOK(0)}
          </span>
        </div>
        {travelTotal > 0 && (
          <div className="flex justify-between items-center gap-2">
            <span className="flex items-center gap-2">
              Herav reise
            </span>
            <span className="tabular-nums">{formatNOK(travelTotal)}</span>
          </div>
        )}
        {editing && (
          <div className="flex justify-between items-center gap-2 pt-1.5 border-t border-border/40">
            <span className="flex items-center gap-2">Oppstart-fakturaandel</span>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                step="5"
                className="h-7 w-16 px-2 text-xs tabular-nums"
                value={startupPct}
                onChange={(e) => setStartupPct(e.target.value)}
                onBlur={() => {
                  const next = Number(startupPct);
                  if (next !== startupValue && next >= 0 && next <= 100) {
                    apply({
                      startup_invoice_pct: next,
                      final_invoice_pct: 100 - next,
                    });
                  }
                }}
              />
              <span>%</span>
            </div>
          </div>
        )}
        {editing && updateMut.isPending && (
          <div className="flex items-center gap-1.5 text-[11px] pt-1">
            <SrIcon name="loader2" className="w-3 h-3 animate-spin" />
            Lagrer...
          </div>
        )}
      </div>
    </div>
  );
}

interface AddLineButtonProps {
  onAdd: () => void;
  disabled?: boolean;
}

export function AddBudgetLineButton({ onAdd, disabled }: AddLineButtonProps) {
  return (
    <tr className="border-b border-border/40">
      <td colSpan={5} className="px-4 py-1.5 pl-8">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs text-muted-foreground hover:text-foreground"
          onClick={onAdd}
          disabled={disabled}
        >
          <SrIcon name="plus" className="w-3 h-3 mr-1" />
          Ny linje
        </Button>
      </td>
    </tr>
  );
}
